import { useAuthStore } from '../store/authStore';

export const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

// Flag to suppress 401 logout during wallet switching
// This prevents race conditions where in-flight requests with old tokens
// trigger logout while a wallet switch is in progress
let suppressLogoutOn401 = false;

export function setSuppressLogoutOn401(value: boolean) {
  suppressLogoutOn401 = value;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  details?: string[]; // Validation error details from backend
}

/**
 * HTTP error with status code preserved, so callers can distinguish
 * 429 rate limits from 500 server errors, etc.
 */
export class ApiHttpError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
  }
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get auth token from localStorage
    const token = localStorage.getItem('xrpl_marketplace_token');

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };

    // Only set Content-Type for requests with a body
    if (options.body) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'include', // Required for CSRF cookies (double-submit pattern)
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Handle 401 - session expired or invalid
        // Don't auto-logout for auth endpoints (login/verify) since 401 there means auth failed, not session expired
        // Also don't logout if suppressed (during wallet switching to avoid race conditions)
        // Also don't redirect if the user was never authenticated to begin with —
        // e.g. a guest visiting a public NFT page that triggers an incidental 401
        // shouldn't get bounced to the homepage.
        const isAuthEndpoint = endpoint.startsWith('/auth/');
        const wasAuthenticated = !!token;
        if (
          response.status === 401 &&
          !isAuthEndpoint &&
          !suppressLogoutOn401 &&
          wasAuthenticated
        ) {
          const { logout } = useAuthStore.getState();
          logout();
          window.location.href = '/';
          throw new Error('Session expired. Please log in again.');
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          // If there are validation details, extract messages and append them
          if (errorData.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
            errorMessage = errorData.details
              .map((d: any) => (typeof d === 'string' ? d : d.message || JSON.stringify(d)))
              .join('. ');
          }
        } catch {
          // Use default error message if JSON parsing fails
        }
        throw new ApiHttpError(errorMessage, response.status);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request error: ${options.method || 'GET'} ${endpoint}`, error);
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
